import React, { Fragment, useState, useCallback } from 'react';
import { FunctionField, TextField, ReferenceField, ReferenceManyField } from 'react-admin';
import Grid from '@material-ui/core/Grid';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { NoPermissions, EmptyResultsDatagrid } from '~/components/ra';
import { productLabel, ProductCreate, ProductShow } from '../Product';
import { useProductTemplates } from '~/hooks/useProductTemplates';
import { useAccountNumberingList } from '~/hooks/useAccountNumberingList';
import RaPagination from '~/components/Pagination/RaPagination';
import { Longdash } from '~/utils';
import { rowsPerPageOptionsDefault, perPageDefault } from '~/utils/pagination';
import { useTranslate } from 'react-admin';
import usePath from '~/hooks/usePath';
import { debounce, set as lodashSet, isEmpty } from 'lodash';
import { SelectInput, GlTypeLabel } from '~/components';
import BankAccountSelect from '~/components/BankAccountSelect';

interface ProductTabProps {
  permissions: any;
  record?: any;
}

const ProductTab = (props: ProductTabProps) => {
  const { permissions, record: recordProp } = props;
  const classes = useStyles();

  const [filters, setFilters] = useState<any>({});
  const [showData, setShowData] = useState(null);
  const handleShowOpen = (data: any) => {
    permissions.includes('solution.product.view') && setShowData(data);
  };
  const handleShowClose = () => setShowData(null);

  const { data: productTemplates } = useProductTemplates();

  const { data: accountNumberingOptions } = useAccountNumberingList();

  const translate = useTranslate();

  const l = productLabel;
  const t = (key: string): string => translate(l(key));

  if (!permissions) return null;
  if (!permissions.includes('solution.product.list')) return <NoPermissions />;
  return (
    <Fragment>
      <ProductShow
        productRecord={showData}
        solutionRecord={recordProp}
        onClose={handleShowClose}
        permissions={permissions}
      />
      <Grid container justify='space-between' alignItems='center' className={classes.spacings}>
        <Grid item>
          <Filters
            {...props}
            filters={filters}
            setFilters={setFilters}
            solutionRecord={recordProp}
          />
        </Grid>
        <Grid item>
          <ProductCreate permissions={permissions} solutionRecord={recordProp} />
        </Grid>
      </Grid>
      <ReferenceManyField
        label='Configurations'
        reference='products'
        target='solutions'
        sort={{ field: 'name', order: 'ASC' }}
        filter={filters}
        pagination={
          <RaPagination
            defaultPerPage={perPageDefault}
            rowsPerPageOptions={rowsPerPageOptionsDefault}
          />
        }
        perPage={perPageDefault}
        {...props}
      >
        <EmptyResultsDatagrid
          emptyText={translate('resources.solutions.fields.noResultsFound')}
          rowClick={(_id: any, _basePath: any, record: any) => handleShowOpen(record)}
          classes={{ row: classes.row }}
        >
          <TextField source='name' label={l('name')} />
          <FunctionField
            label={l('template')}
            render={(record: any) => {
              const productTemplate = productTemplates?.find(
                (pt) => pt.code === record.templateCode
              );
              if (!productTemplate) return t('productTemplateMissing');
              return <GlTypeLabel glType={productTemplate.glType} text={productTemplate.alias} />;
            }}
            sortable={false}
            // sortBy='templateCode'
          />
          <ReferenceField
            source='bankAccountId'
            reference='bank/accounts'
            basePath='/bank/accounts'
            link={false}
            sortable={false}
            addLabel={false}
            emptyText={Longdash}
            label={l('liquidityBucket')}
            sortBy='bankAccountId'
          >
            <TextField source='alias' />
          </ReferenceField>
          <TextField source='currency' label={l('currency')} />
          <FunctionField
            label={l('accountNumbering')}
            render={(record: any) => {
              return (
                accountNumberingOptions?.find((an) => an.id === record.accountNumbering)?.name ||
                record.accountNumbering
              );
            }}
            sortable={false}
          />
          <TextField source='productCode' label={l('productCode')} emptyText={Longdash} />
        </EmptyResultsDatagrid>
      </ReferenceManyField>
    </Fragment>
  );
};

const Filters = (props: any) => {
  const { filters, setFilters, solutionRecord } = props;
  const classes = useStyles();
  const translate = useTranslate();
  const t = (key: string) => translate(`resources.solutions.products.${key}`);
  const td = (key: string) => translate(`utils.${key}`);

  const { changePath } = usePath();
  const debouncedSetFilters = useCallback(debounce(setFilters, 400), [setFilters]);
  const handleChange = (e: any) => {
    if (!e?.target) return null;
    const { name, value } = e.target;
    changePath({ page: 1 });
    debouncedSetFilters((prev: any) => {
      return { ...lodashSet(prev, name, value) };
    });
  };

  const { data: productTemplates } = useProductTemplates();

  const productTemplateOptions = (() => {
    const solutionProductTemplates: string[] = solutionRecord?.productTemplates || [];
    const options = productTemplates
      ?.filter((pt) => !!solutionProductTemplates.find((code) => pt.code === code))
      .map((pt) => ({ value: pt.code, text: pt.alias }));

    if (!isEmpty(options)) {
      return options;
    } else {
      return [{ value: undefined, text: t('noData') }];
    }
  })();

  return (
    <form className={classes.filters}>
      <SelectInput
        options={productTemplateOptions as any}
        name='templateCode'
        source='templateCode'
        label={t('template')}
        value={filters.templateCode ?? ''}
        emptyText={td('any')}
        onChange={handleChange}
        className={classes.formControl}
        disableHelperText
        allowEmpty
      />
      <BankAccountSelect
        value={filters.bankAccountId ?? ''}
        onChange={handleChange}
        source='bankAccountId'
        name='bankAccountId'
        label={t('liquidityBucket')}
        className={classes.formControl}
        emptyText={td('any')}
        disableHelperText
        allowEmpty
      />
    </form>
  );
};

const useStyles = makeStyles((theme) => ({
  spacings: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  row: {
    '& #edit-button': {
      opacity: 0.5,
      transition: `opacity ${theme.transitions.easing.easeIn}`,
      transitionDuration: `${theme.transitions.duration.shortest}ms`,
    },
    '&:hover': {
      '& #edit-button': {
        opacity: 1,
      },
    },
  },
  filters: {
    alignItems: 'flex-start',
    display: 'flex',
    justifyContent: 'space-between',
  },
  formControl: {
    marginRight: theme.spacing(1),
    minWidth: 150,
    maxWidth: 270,
    '&:last-child': {
      marginRight: 0,
    },
  },
}));

export default ProductTab;
